import { useEffect } from 'react';
import { motion } from 'framer-motion';
import Footer from "../../components/Footer"
import { ArrowLeft } from 'react-feather';
import { useNavigate } from 'react-router-dom';

export default function TerminosCondiciones() {
  const navigate = useNavigate();
  const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const Section = ({ number, title, children }) => (
    <div className="mb-12 border-b border-white/5 pb-10 last:border-0">
      <h2 className="text-xl md:text-2xl font-bold text-white mb-4 flex items-center gap-3">
        <span className="text-xs font-mono text-white/40 border border-white/20 px-2 py-1 rounded">{number}</span>
        {title}
      </h2>
      <div className="text-white/60 font-light leading-relaxed space-y-4 pl-0 md:pl-11 text-justify">
        {children}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#030303] text-white font-sans selection:bg-white selection:text-black">
      
      {/* NOISE TEXTURE */}
      <div className="fixed inset-0 z-[50] pointer-events-none opacity-[0.03] mix-blend-overlay" 
           style={{ backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")' }}>
      </div>

      {/* Header / Nav */}
      <div className="fixed top-0 left-0 w-full z-40 px-6 py-6 flex justify-between items-center bg-gradient-to-b from-[#030303] to-transparent pointer-events-none">
        <button 
            onClick={() => navigate(-1)} 
            className="pointer-events-auto flex items-center gap-2 text-sm font-bold tracking-widest text-white/50 hover:text-white transition-colors group"
        >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            VOLVER
        </button>
      </div>

      <div className="relative z-10 pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          
          <motion.div 
            className="mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs font-bold text-white/40 tracking-[0.2em] uppercase block mb-4">Legal</span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight text-white">
              Términos y <br/> <span className="text-white/30">Condiciones.</span>
            </h1>
            <p className="text-sm text-white/40 font-mono">
              Vigente desde: {today} | Lima, Perú.
            </p>
          </motion.div>

          <motion.div 
            className="relative"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="prose prose-invert max-w-none">
                <p className="text-lg text-white/80 leading-relaxed mb-12 font-light">
                  Bienvenido a <span className="font-bold text-white">AFTR</span>. Al registrarte y utilizar nuestra plataforma, aceptas celebrar un contrato vinculante con nosotros bajo la normativa peruana vigente.
                </p>

                <Section number="01" title="Naturaleza del Servicio">
                  <p>
                    AFTR actúa exclusivamente como un <strong>intermediario tecnológico</strong> (Marketplace) que conecta a usuarios con organizadores de eventos y establecimientos de entretenimiento ("Organizadores").
                  </p>
                  <p>
                    AFTR <strong>NO organiza, produce ni supervisa</strong> los eventos listados, salvo que se indique expresamente lo contrario. Por ende, no somos responsables por la calidad, seguridad, o ejecución del evento físico.
                  </p>
                </Section>

                <Section number="02" title="Registro y Veracidad">
                    <ul className="list-disc list-inside space-y-2 marker:text-white/30">
                        <li>Declaras ser mayor de 18 años. El ingreso a eventos con venta de alcohol está estrictamente prohibido para menores de edad.</li>
                        <li>La información proporcionada (DNI, Nombre Completo) tiene carácter de Declaración Jurada. El uso de datos falsos conllevará la suspensión inmediata de la cuenta.</li>
                    </ul>
                </Section>

                <Section number="03" title="Política de Accesos y QR">
                    <p>
                      El código QR generado es personal e intransferible. AFTR implementa medidas de seguridad (actualización dinámica) para evitar fraudes.
                    </p>
                    <p>
                      <strong>Derecho de Admisión:</strong> La adquisición de una entrada o lista (QR) a través de AFTR no garantiza el ingreso si el usuario incumple las normas de vestimenta, conducta o estado etílico establecidas por el Organizador o el local, quienes se reservan el derecho de admisión. AFTR no realizará reembolsos en estos casos.
                    </p>
                </Section>

                <Section number="04" title="Protección de Datos (Ley 29733)">
                    <p>
                        De conformidad con la <strong>Ley N° 29733, Ley de Protección de Datos Personales</strong>, y su Reglamento, autorizas a AFTR al tratamiento de tus datos personales para: (i) Gestión de acceso y seguridad; (ii) Envío de confirmaciones; y (iii) Fines estadísticos anónimos.
                    </p>
                    <p>
                        <strong>Tus datos serán compartidos estrictamente con el Organizador</strong> del evento al que te registres para fines de control de identidad en puerta. No vendemos tu información a terceros externos.
                    </p>
                    <p>
                        <strong>Derechos ARCO:</strong> Como titular de tus datos, puedes ejercer tus derechos de Acceso, Rectificación, Cancelación y Oposición enviando un correo a <span className="text-white border-b border-white/20">privacidad@goclub.social</span>.
                    </p>
                </Section>

                <Section number="05" title="Política de Reembolsos y Cancelaciones">
                    <p>
                        Dado que AFTR es un intermediario de recaudo:
                    </p>
                    <ul className="list-disc list-inside space-y-2 marker:text-white/30">
                        <li>Si un evento es cancelado, el responsable legal de la devolución del dinero es el <strong>Organizador</strong>.</li>
                        <li>AFTR gestionará el proceso de devolución solo cuando los fondos aún se encuentren en nuestra pasarela de pagos.</li>
                        <li>No se aceptan cambios ni devoluciones por errores del usuario al comprar (fechas o eventos equivocados).</li>
                    </ul>
                </Section>

                <Section number="06" title="Propiedad Intelectual">
                    <p>
                        Todo el software, diseño, y código fuente de AFTR está protegido por derechos de autor. Queda prohibida la ingeniería inversa, copia o distribución no autorizada.
                    </p>
                </Section>

                <Section number="07" title="Jurisdicción y Libro de Reclamaciones">
                    <p>
                        Estos términos se rigen por las leyes de la República del Perú. Para cualquier controversia, las partes se someten a los jueces y tribunales del Cercado de Lima.
                    </p>
                    <p className="mt-4 pt-4 border-t border-white/10 text-sm text-white/50">
                        Conforme al Código de Protección y Defensa del Consumidor, contamos con un <a href="#" className="text-white underline decoration-white/30 hover:decoration-white">Libro de Reclamaciones Virtual</a> a tu disposición.
                    </p>
                </Section>

            </div>
          </motion.div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}